import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useFilters } from "@/lib/filters";
import { fmtCurrency, fmtRelative, fmtValue } from "@/lib/format";
import {
  DeltaBadge,
  EmptyState,
  ErrorState,
  KpiCard,
  Leaderboard,
  LoadingState,
  PageHeader,
  Panel,
  SourceBadge,
  StatusPill,
} from "@/components/dash/ui";
import SalesOverview from "@/components/dash/SalesPanels";
import { DailySummaryBlock, ForecastRow, InsightsStrip, TargetsRow } from "@/components/dash/exec";
import { TrendQuad } from "@/components/dash/charts";
import { Link } from "react-router-dom";

export default function ExecutivePage() {
  const { range, preset } = useFilters();
  const exec = useQuery({ queryKey: ["executive", range], queryFn: () => dashApi.executive(range) });
  const trends = useQuery({ queryKey: ["trends", range], queryFn: () => dashApi.trends(range) });
  const boards = useQuery({ queryKey: ["leaderboards", range], queryFn: () => dashApi.leaderboards(range) });
  const activity = useQuery({ queryKey: ["recent-activity"], queryFn: dashApi.recentActivity });
  const insights = useQuery({ queryKey: ["insights"], queryFn: dashApi.insights });
  const salesTrend = useQuery({ queryKey: ["trend", "odoo.sales_incl"], queryFn: () => dashApi.historyTrends("odoo.sales_incl") });
  const digest = useQuery({ queryKey: ["digest"], queryFn: dashApi.digest });
  const daily = useQuery({ queryKey: ["daily-summary"], queryFn: dashApi.dailySummary });
  const targets = useQuery({ queryKey: ["targets"], queryFn: dashApi.targets });
  const forecast = useQuery({ queryKey: ["forecast"], queryFn: dashApi.forecast });
  const custRank = useQuery({ queryKey: ["customer-rankings"], queryFn: dashApi.customerRankings });
  const custConc = useQuery({ queryKey: ["customer-concentration"], queryFn: dashApi.customerConcentration });

  const alerts = exec.data?.alerts_summary ?? {};
  const win = exec.data?.window;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Executive Overview"
        description="Live business KPIs and failures across every BGR system."
        right={
          win && (
            <div className="text-right text-xs text-slate-500">
              <div className="text-slate-300">
                {win.from} → {win.to} · {win.days}d
              </div>
              <div>{win.mode === "live" ? "Live query" : "From latest snapshots"}</div>
            </div>
          )
        }
      />

      {(alerts.critical || alerts.warning) ? (
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-ink-500/60 bg-ink-800/60 px-5 py-3">
          <span className="text-sm font-medium text-slate-300">Open alerts:</span>
          {alerts.critical ? <StatusPill status="critical" label={`${alerts.critical} critical`} /> : null}
          {alerts.warning ? <StatusPill status="warning" label={`${alerts.warning} warning`} /> : null}
          {alerts.info ? <StatusPill status="info" label={`${alerts.info} info`} /> : null}
          <a href="/failures" className="ml-auto text-xs font-medium text-brand-300 hover:text-brand-200">
            View failures →
          </a>
        </div>
      ) : null}

      {exec.isLoading ? (
        <LoadingState />
      ) : exec.isError ? (
        <ErrorState error={exec.error} />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-3">
          {exec.data?.cards.map((c) => (
            <KpiCard
              key={c.key}
              label={c.label}
              value={c.value}
              unit={c.unit}
              source={c.source}
              tone={c.tone}
              delta={c.delta ? { pct: c.delta.pct } : null}
              invertDelta={c.key === "overdue_repairs"}
            />
          ))}
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Executive insights</h2>
        {insights.isLoading ? <LoadingState /> : <InsightsStrip insights={insights.data?.insights ?? []} />}
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Today" subtitle="vs yesterday / last week / last month" right={daily.data && <span className="text-xs text-slate-500">live</span>}>
          {daily.isLoading ? <LoadingState label="Computing daily figures…" /> : daily.isError ? <ErrorState error={daily.error} /> : daily.data ? <DailySummaryBlock data={daily.data} /> : null}
        </Panel>
        <Panel title="KPI targets" subtitle="actual vs target">
          {targets.isLoading ? <LoadingState /> : targets.data ? <TargetsRow data={targets.data} /> : null}
        </Panel>
      </div>

      {digest.data && (
        <Panel
          title="Reports & delivery"
          right={<Link to="/digest" className="text-xs font-medium text-brand-300 hover:text-brand-200">Digest →</Link>}
        >
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Latest report</p>
              <p className="mt-0.5 text-sm text-slate-200">{digest.data.latest_report?.title ?? "—"}</p>
              <p className="text-[11px] text-slate-500">{digest.data.latest_report ? fmtRelative(digest.data.latest_report.generated_at) : "none yet"}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Next scheduled</p>
              <p className="mt-0.5 text-sm text-slate-200">{digest.data.next_report?.kind ?? "—"}</p>
              <p className="text-[11px] text-slate-500">{digest.data.next_report ? fmtRelative(digest.data.next_report.next_run_at) : "pending"}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Critical alerts</p>
              <p className={`mt-0.5 text-2xl font-semibold ${digest.data.critical_alerts ? "text-rose-300" : "text-white"}`}>{digest.data.critical_alerts}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Last delivery</p>
              <p className="mt-0.5 text-sm text-slate-200">{digest.data.recent_deliveries[0]?.status ?? "—"}</p>
              <p className="text-[11px] text-slate-500">{digest.data.recent_deliveries[0] ? fmtRelative(digest.data.recent_deliveries[0].created_at) : "no deliveries"}</p>
            </div>
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel
          title="Sales trend (historical)"
          subtitle="week / month / quarter / year on year"
          right={<Link to="/trends" className="text-xs font-medium text-brand-300 hover:text-brand-200">All trends →</Link>}
        >
          {salesTrend.isLoading ? <LoadingState /> : salesTrend.data ? <TrendQuad trends={salesTrend.data} /> : null}
        </Panel>
        <Panel title="Forecast" subtitle="trend-based projection (no AI), directional">
          {forecast.isLoading ? <LoadingState /> : forecast.data ? <ForecastRow data={forecast.data} /> : null}
        </Panel>
      </div>

      {exec.data?.sales && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Business sales {win ? `· ${win.from} → ${win.to}` : ""}</h2>
          <SalesOverview sales={exec.data.sales} />
        </section>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel
          title="Period-over-period"
          subtitle={preset === "30d" ? "vs previous 30 days" : "vs previous equal period"}
          className="lg:col-span-2"
        >
          {trends.isLoading ? (
            <LoadingState />
          ) : trends.isError ? (
            <ErrorState error={trends.error} />
          ) : (
            <ul className="divide-y divide-ink-500/40">
              {trends.data?.series.map((s) => (
                <li key={s.key} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2.5">
                    <SourceBadge code={s.source} />
                    <span className="text-sm text-slate-300">{s.label}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="tabular-nums text-sm font-medium text-white">
                      {fmtValue(s.current, s.label.includes("EUR") ? "EUR" : "count")}
                    </span>
                    <span className="w-24 text-right">
                      <DeltaBadge pct={s.pct} />
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Recent activity">
          {activity.isLoading ? (
            <LoadingState />
          ) : !activity.data?.items.length ? (
            <EmptyState compact>No recent activity.</EmptyState>
          ) : (
            <ul className="space-y-2.5">
              {activity.data.items.slice(0, 10).map((a, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <SourceBadge code={a.source} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-200">{a.title}</p>
                    {a.subtitle && <p className="truncate text-xs text-slate-500">{a.subtitle}</p>}
                  </div>
                  <span className="whitespace-nowrap text-[11px] text-slate-500">{fmtRelative(a.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {(boards.data?.boards ?? []).slice(0, 3).map((b) => (
          <Panel key={b.key} title={b.label} right={<SourceBadge code={b.source} />}>
            <Leaderboard rows={b.rows} unit={b.unit} />
          </Panel>
        ))}
        {boards.isLoading && <LoadingState />}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel
          title="Top customers"
          subtitle="by total spend across all systems"
          className="lg:col-span-2"
          right={<Link to="/customers" className="text-xs font-medium text-brand-300 hover:text-brand-200">All customers →</Link>}
        >
          {custRank.isLoading ? <LoadingState /> : !custRank.data?.highest_value.length ? (
            <EmptyState compact>No customer data yet — run a sync.</EmptyState>
          ) : (
            <ol className="space-y-1">
              {custRank.data.highest_value.slice(0, 6).map((c, i) => (
                <li key={c.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-ink-700/60">
                  <span className="w-5 text-center text-xs font-semibold text-slate-500">{i + 1}</span>
                  <Link to={`/customers/${c.id}`} className="flex-1 truncate text-sm text-slate-200 hover:text-brand-300">{c.name || "—"}</Link>
                  <span className="text-[11px] text-slate-500">{c.services_used} svc</span>
                  <span className="tabular-nums text-sm font-medium text-white">{fmtCurrency(c.total_spend)}</span>
                </li>
              ))}
            </ol>
          )}
        </Panel>
        <Panel
          title="Revenue concentration"
          right={<Link to="/customers/concentration" className="text-xs font-medium text-brand-300 hover:text-brand-200">Detail →</Link>}
        >
          {custConc.isLoading ? <LoadingState /> : custConc.data ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Risk</span>
                <StatusPill status={custConc.data.risk === "high" ? "critical" : custConc.data.risk === "medium" ? "warning" : "ok"} label={`${custConc.data.risk.toUpperCase()}`} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Top customer</span>
                <span className="tabular-nums text-white">{custConc.data.top1_pct != null ? `${custConc.data.top1_pct.toFixed(1)}%` : "—"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Top 10</span>
                <span className="tabular-nums text-white">{custConc.data.top10_pct != null ? `${custConc.data.top10_pct.toFixed(1)}%` : "—"}</span>
              </div>
              <p className="text-[11px] text-slate-500">{fmtCurrency(custConc.data.total_revenue)} across {custConc.data.customer_count} customers</p>
            </div>
          ) : <EmptyState compact>No data.</EmptyState>}
        </Panel>
      </div>
    </div>
  );
}
